/**
 * EditorialHero — primitive Editorial Operations (HiFi.3).
 *
 * Bloc de tête d'écran récurrent. Patron type :
 *
 *   [ModeBadge — petite pastille + libellé MAJUSCULE]
 *   [Date-line en Fraunces italic 13 px, --ink-soft]
 *   [Titre DM Serif 44 px mobile / 56 px desktop]
 *   [Accent en Fraunces italic, couleur --accent-deep, suivi d'un point]
 *
 * Le « pivot italic » sur l'accent (typiquement le prénom du parent
 * ou un mot fort de la date) est la signature visuelle Editorial
 * Operations. Conserver impérativement.
 *
 * Variantes d'usage :
 *   - title="Bonjour" + accent="Camille"   → « Bonjour, Camille. »
 *   - title="Du" + accent="11 au 17 mai"   → « Du 11 au 17 mai. »
 *   - title="Album," + accent="famille"    → « Album, famille. »
 *
 * Le composant ne touche pas à `getCurrentHome()` — l'appelant
 * compose les chaînes selon le contexte et passe ce qui doit
 * apparaître. `dateLine`, `badge`, `trailing` sont tous optionnels.
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './editorial.module.css';
import { modeBadge, type ModeBadgeProps } from './ModeBadge.js';

export interface HeroProps {
  /** Badge mode-foyer optionnel (au-dessus du dateLine). */
  badge?: ModeBadgeProps;
  /** Sous-ligne en Fraunces italic — souvent une date ou un kicker secondaire. */
  dateLine?: string;
  /** Première partie du titre (sans virgule). */
  title: string;
  /** Mot d'accent en Fraunces italic, posé après une virgule et suivi d'un point. */
  accent?: string;
  /**
   * Couleur de l'accent. Par défaut `var(--accent-deep)` qui suit le
   * mode courant. À overrider seulement si on veut casser la palette
   * mode (rare — ex. en onboarding où le mode n'est pas encore choisi).
   */
  accentColor?: string;
  /** Contenu à droite (boutons « Inviter », « Nouvelle entrée », etc.). */
  trailing?: TemplateResult | string;
  /**
   * Insère une virgule entre `title` et `accent` (pivot « Bonjour,
   * Camille »). Défaut `false` : la plupart des titres forment une
   * phrase continue avec l'accent (« Du 11 au 17 mai », « Sac à dos »)
   * et ne veulent PAS de virgule. Les titres qui en veulent une la
   * portent dans leur propre chaîne (ex. « Album, »).
   */
  pivotComma?: boolean;
  /**
   * Alignement vertical du `trailing`. `'head'` (défaut) : en haut à
   * droite, aligné avec le badge / la date-line — comportement
   * historique (boutons « Inviter », « Nouvelle entrée »…). `'center'` :
   * centré verticalement en regard du **titre** (utilisé par le widget
   * météo de l'accueil, aligné avec « Bonjour »).
   */
  trailingAlign?: 'head' | 'center';
  /** testid optionnel pour les E2E. */
  testid?: string;
}

export function editorialHero(props: HeroProps): TemplateResult {
  const {
    badge,
    dateLine,
    title,
    accent,
    accentColor = 'var(--accent-deep)',
    trailing,
    pivotComma = false,
    trailingAlign = 'head',
    testid,
  } = props;
  const headRow = html`<div class=${styles.heroHead}>
    <div>
      ${badge ? modeBadge(badge) : null}
      ${dateLine ? html`<div class=${styles.heroDate}>${dateLine}</div>` : null}
    </div>
    ${trailingAlign === 'head' ? (trailing ?? null) : null}
  </div>`;
  const titleH1 = html`<h1 class=${styles.heroTitle}>
    ${title}${accent
      ? html`${pivotComma ? ',' : ''}<br /><span
            class=${styles.heroAccent}
            style="color: ${accentColor}"
            >${accent}.</span
          >`
      : null}
  </h1>`;
  // Variante « trailing centré » : le hero devient une rangée flex où
  // le trailing (widget météo) est centré verticalement en regard du
  // titre — aligné avec « Bonjour ». Les autres usages gardent le
  // trailing en haut (comportement historique inchangé).
  if (trailing && trailingAlign === 'center') {
    return html`<header
      class="${styles.hero} ${styles.heroSplit}"
      data-testid=${testid ?? 'editorial-hero'}
    >
      <div class=${styles.heroLead}>${headRow}${titleH1}</div>
      <div class=${styles.heroTrailing}>${trailing}</div>
    </header>`;
  }
  return html`<header class=${styles.hero} data-testid=${testid ?? 'editorial-hero'}>
    ${headRow}${titleH1}
  </header>`;
}
